import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Upload, Trash2, Copy, Check, Image as ImageIcon } from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { PageLoader } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/common/EmptyState';
import { useMedia, useUploadMedia, useDeleteMedia } from '@/hooks/useMedia';
import { formatFileSize } from '@/lib/utils';
import { useTranslation } from '@/features/i18n';
import type { Media } from '@/types/database';

export function MediaPage() {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [mediaToDelete, setMediaToDelete] = useState<Media | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: media, isLoading } = useMedia();
  const uploadMedia = useUploadMedia();
  const deleteMedia = useDeleteMedia();
  const { t } = useTranslation();

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    for (const file of Array.from(files)) {
      await uploadMedia.mutateAsync({ file });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (mediaToDelete) {
      await deleteMedia.mutateAsync({
        id: mediaToDelete.id,
        filePath: mediaToDelete.file_path,
      });
      setDeleteModalOpen(false);
      setMediaToDelete(null);
    }
  };

  const handleCopyUrl = async (url: string, id: string) => {
    await navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <SEO title={t.admin.media.title} noIndex />

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{t.admin.media.title}</h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              {t.admin.media.subtitle}
            </p>
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleUpload}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              isLoading={uploadMedia.isPending}
              leftIcon={<Upload className="w-4 h-4" />}
            >
              {t.admin.media.upload}
            </Button>
          </div>
        </div>

        {media && media.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3 md:gap-4">
            {media.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2, delay: index * 0.02 }}
              >
                <Card padding="none" className="group relative overflow-hidden touch-manipulation">
                  {item.file_type?.startsWith('image/') ? (
                    <img
                      src={item.public_url}
                      alt={item.alt_text || item.file_name}
                      className="w-full aspect-square object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full aspect-square bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 sm:w-8 sm:h-8 text-neutral-400" />
                    </div>
                  )}

                  {/* Overlay - always visible on touch devices, hover on desktop */}
                  <div className="absolute inset-0 bg-black/60 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1 sm:gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-white hover:bg-white/20 active:bg-white/30 p-2 sm:p-2"
                      onClick={() => handleCopyUrl(item.public_url, item.id)}
                    >
                      {copiedId === item.id ? (
                        <Check className="w-4 h-4 sm:w-4 sm:h-4" />
                      ) : (
                        <Copy className="w-4 h-4 sm:w-4 sm:h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-white hover:bg-red-500/50 active:bg-red-500/70 p-2 sm:p-2"
                      onClick={() => {
                        setMediaToDelete(item);
                        setDeleteModalOpen(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4 sm:w-4 sm:h-4" />
                    </Button>
                  </div>

                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-1.5 sm:p-2">
                    <p className="text-white text-[10px] sm:text-xs truncate">{item.file_name}</p>
                    {item.file_size && (
                      <p className="text-white/70 text-[10px] sm:text-xs">
                        {formatFileSize(item.file_size)}
                      </p>
                    )}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <EmptyState
            title={t.admin.media.noFiles}
            description={t.admin.media.noFilesDesc}
            action={
              <Button
                onClick={() => fileInputRef.current?.click()}
                leftIcon={<Upload className="w-4 h-4" />}
              >
                {t.admin.media.uploadFirst}
              </Button>
            }
          />
        )}
      </div>

      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t.admin.media.deleteFile}
      >
        <div className="p-6">
          <p className="text-neutral-600 dark:text-neutral-400 mb-6">
            {t.admin.common.confirmDelete.replace('{name}', mediaToDelete?.file_name || '')}
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={deleteMedia.isPending}>
              {t.common.delete}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
